import { io, Socket } from "socket.io-client";
import { useSelector } from "react-redux";
import { useState } from "react";
import { notification, useDidMount } from "@epcnetwork/core-ui-kit";

import { RootState } from "store";
import { appEnvironment } from "config/environment.config";
import {
  JobListListenerEventsKeys,
  JobListListenerHandlers,
  JobListSocketInstance,
} from "./use-job-list-socket.types";

const SOCKET_JOB_LIST_NAMESPACE = "/job";

export const useJobListSocketHook = () => {
  const [socket, setSocket] = useState<JobListSocketInstance | null>(null);

  const [isConnected, setConnected] = useState<boolean>(false);

  const { token } = useSelector((state: RootState) => state.auth);

  const listenerHandlers: Partial<JobListListenerHandlers> = {
    connect: () => {
      setConnected(true);
    },
    connectError: () => {
      setConnected(false);
    },
    disconnect: () => {
      setConnected(false);
    },
    exception: (error: Error) => {
      notification.error("Socket exception", "Error while connecting to the socket. " + error);
    },
  };

  const addJobListener =
    (socketInstance: Socket<Partial<JobListListenerHandlers>>) =>
    <E extends JobListListenerEventsKeys>(event: E) => {
      socketInstance.on<JobListListenerEventsKeys>(event, listenerHandlers[event]);

      return () => {
        socketInstance.off<JobListListenerEventsKeys>(event, listenerHandlers[event]);
      };
    };

  const mountListeners = (socketInstance: Socket<JobListListenerHandlers>) => {
    const connectUnmount = addJobListener(socketInstance)("connect");
    const connectErrorUnmount = addJobListener(socketInstance)("connectError");
    const disconnectUnmount = addJobListener(socketInstance)("disconnect");
    const exceptionUnmount = addJobListener(socketInstance)("exception");

    return () => {
      connectUnmount();
      connectErrorUnmount();
      disconnectUnmount();
      exceptionUnmount();
    };
  };

  useDidMount(() => {
    const socketInstance: Socket<JobListListenerHandlers> = io(
      appEnvironment.apiUrl + SOCKET_JOB_LIST_NAMESPACE,
      {
        autoConnect: false,
        extraHeaders: {
          authorization: `Bearer ${token}`,
        },
      },
    );

    const unmountListeners = mountListeners(socketInstance);
    socketInstance.connect();

    setSocket(socketInstance);

    return () => {
      socket?.disconnect();
      unmountListeners();
    };
  });

  return { socket, isConnected };
};
